/**
 * Composable für Benachrichtigungsfunktionen
 * Zentralisiert alle Funktionen für Benachrichtigungen (Sound, Desktop, etc.)
 */

import { ref } from 'vue';

export function useNotifications() {
  // Audio-Element für Benachrichtigungstöne
  const notificationSound = ref(null);
  const orderNotificationSound = ref(null);
  
  // Standard-Fallback-Sound als Data-URI
  const notificationSoundDataUri = 'data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YQAAAAA='; 

  /**
   * Sound-Element initialisieren
   * @param {HTMLElement} soundElement - Optionales Audio-Element aus dem Template
   */
  const initializeSound = (soundElement = null) => {
    if (soundElement) {
      notificationSound.value = soundElement;
      notificationSound.value.src = notificationSoundDataUri;
    } else if (!notificationSound.value) {
      // Falls kein Element übergeben, erstelle ein neues
      notificationSound.value = new Audio(notificationSoundDataUri);
    }
    
    // Initialisiere auch den speziellen Bestellungs-Sound
    initializeOrderSound();
  };

  /**
   * Spezifischen Bestellungs-Sound initialisieren
   */
  const initializeOrderSound = () => {
    if (!orderNotificationSound.value) {
      orderNotificationSound.value = new Audio('/sounds/ensu-bip.mp3');
      orderNotificationSound.value.preload = 'auto';
      
      // Fallback auf Standard-Sound bei Fehlern
      orderNotificationSound.value.onerror = () => {
        console.warn("Ensu-bip.mp3 konnte nicht geladen werden, verwende Standard-Sound");
        orderNotificationSound.value = new Audio(notificationSoundDataUri);
      };
    }
  };

  /**
   * Standard-Benachrichtigungston abspielen
   */
  const playNotificationSound = () => {
    console.log("Versuche, Standard-Benachrichtigungston abzuspielen...");
    if (!notificationSound.value) {
      initializeSound();
    }

    notificationSound.value.play().catch(err => {
      console.warn("Standard-Benachrichtigungston konnte nicht abgespielt werden:", err);
    });
  };

  /**
   * Spezifischen Bestellungs-Sound abspielen (Ensu-Bip)
   */
  const playOrderNotificationSound = () => {
    console.log("Versuche, Ensu-Bip Bestellungs-Sound abzuspielen...");
    if (!orderNotificationSound.value) {
      initializeOrderSound();
    }

    // Sound abspielen mit Fallback
    orderNotificationSound.value.play().catch(err => {
      console.warn("Ensu-Bip Sound konnte nicht abgespielt werden, verwende Standard-Sound:", err);
      // Fallback auf Standard-Sound
      playNotificationSound();
    });
  };

  /**
   * Desktop-Benachrichtigung anzeigen
   * @param {string} title - Titel der Benachrichtigung
   * @param {string} body - Text der Benachrichtigung
   * @param {string} icon - Pfad zum Icon (optional)
   */
  const showDesktopNotification = (title, body, icon = '/favicon.ico') => {
    // Prüfen, ob Desktop-Benachrichtigungen unterstützt werden
    if (!("Notification" in window)) {
      console.warn("Desktop-Benachrichtigungen werden von diesem Browser nicht unterstützt");
      return;
    }

    // Prüfen der Berechtigungen
    if (Notification.permission === "granted") {
      // Benachrichtigung anzeigen
      new Notification(title, { body, icon });
    } else if (Notification.permission !== "denied") {
      // Berechtigungen anfordern
      Notification.requestPermission().then(permission => {
        if (permission === "granted") {
          new Notification(title, { body, icon });
        }
      });
    }
  };

  /**
   * Berechtigung für Benachrichtigungen anfordern
   * @returns {Promise<string>} - Berechtigungsstatus
   */
  const requestNotificationPermission = async () => {
    if (!("Notification" in window)) {
      return "unsupported";
    }

    if (Notification.permission !== "granted" && Notification.permission !== "denied") {
      try {
        return await Notification.requestPermission();
      } catch (err) {
        console.error('Fehler beim Anfordern der Benachrichtigungsberechtigung:', err);
        return "error";
      }
    }

    return Notification.permission;
  };

  return {
    notificationSound,
    orderNotificationSound,
    initializeSound,
    initializeOrderSound,
    playNotificationSound,
    playOrderNotificationSound,
    showDesktopNotification,
    requestNotificationPermission
  };
} 